import Configuration from '@/agilis/util/Configuration'
import dialogFormMixin from '@/agilis/components/DialogForm/dialogFormMixin'
import jsonLogic from '@/plugins/jsonLogic'
import L from 'lodash'
import Vue from 'vue'
import ResizableContainer from '@/agilis/components/ResizableContainer/ResizableContainer.vue'
import FormTree from '@/agilis/components/Form/FormTree/FormTree.vue'

import { mapActions, mapGetters } from 'vuex'
import JsonEditorText from '@/agilis/components/JsonEditorText/JsonEditorText.vue'

const d = require('deepdash').default
d(L)

function replaceIdStr (id, prevId, nextId) {
  if (id === prevId) { //  exact match
    return nextId
  } else if (id.indexOf(prevId + '-') === 0) { // begin of the ID
    return id.replace(prevId + '-', nextId + '-')
  } else if ('-' + id.indexOf(prevId + '-') > 0) { // inside the ID
    return id.replace('-' + prevId + '-', '-' + nextId + '-')
  }

  return id
}

export default {
  name: 'TreeEditor',
  mixins: [dialogFormMixin],

  components: {
    JsonEditorText,
    ResizableContainer,
    FormTree
  },

  props: {
    document: Object,
    schema: Object,
    applicationId: String,
    customComponents: Object
  },

  data () {
    return {
      json: null,
      jsonChanged: false,
      activeNodes: [],
      openNodes: [],
      forceUnmount: false,
      currentVersion: '',
      autoSave: null,
      expanded: false,
      formTree: [],
      dialogDuplicate: false,
      nodeToCopy: null,
      destinationNodeId: null
    }
  },

  beforeDestroy () {
    clearInterval(this.autoSave)
  },

  computed: {
    ...mapGetters({
      loading: 'loaderStore/loading',
      selectedContext: 'contextStore/selectedContext',
      userId: 'userDataStore/userId',
      userIsAdmin: 'userDataStore/userIsAdmin'
    }),

    expandIconTitle () {
      return this.expanded ? 'collapse' : 'expand'
    },

    duplicateDialogTitle () {
      return this.$t('label.treeEditorDuplicateTitle')
    },

    activeNodeId () {
      return this.activeNodes[0]
    },

    activeNode () {
      const nodeId = this.activeNodes[0]
      const node = nodeId && this.$refs?.tree?.nodes[nodeId]?.item
      if (node) this.checkNodeConsistency(node)

      return node || null
    },

    activeNodeForm () {
      const node = this.activeNode

      if (node) {
        const schema = this.getSchemaById(node._schema)
        const { columns, formRows } = this.getSchemaForm(schema, node, 'edit')

        const formModel = { model: node, fields: L.cloneDeep(columns), formRows }

        return formModel
      }
      return null
    },

    documentTitle () {
      return this.document.name ? this.$t(this.document.name) : this.document.id
    },

    documentDescription () {
      return this.document.description && this.$t(this.document.description)
    },

    migrateWpDisabled () {
      return !this.document.preprocessApi
    },

    checkinDisabled () {
      return !this.json._checkoutFlag || (this.json._lastUpdatedUser !== this.userId && this.json._checkoutFlag)
    },

    checkoutDisabled () {
      return this.json._lastUpdatedUser !== this.userId && this.json._checkoutFlag
    },

    checkDocumentDisabled () {
      return !this.json._checkoutFlag || this.checkoutDisabled
    },

    jsonLogicContext () {
      return {
        node: this.activeNode
      }
    }
  },

  watch: {
    document: {
      handler (newValue) {
        newValue && this.schema && this.loadTree() // Load dell'ultima versione del doc da server
      },
      immediate: true
    },
    json: {
      handler (newValue, oldValue) {
        if (newValue) this.loadFormTree()
        if (oldValue) this.jsonChanged = true
      },
      deep: true
    }
  },

  methods: {
    ...mapActions({
      'notifyError': 'notificationStore/notifyError',
      'notifyMessage': 'notificationStore/notifyMessage',
      'notifyUserConfirm': 'notificationStore/notifyUserConfirm',
      'startLoader': 'loaderStore/startLoader',
      'stopLoader': 'loaderStore/stopLoader'
    }),

    // /////////////////////////////////////////////////////////////
    // Input/Output utils
    // /////////////////////////////////////////////////////////////
    async loadTree () {
      const loader = { id: 'load-json-document' }

      try {
        this.startLoader(loader)

        const url = `/api/${this.applicationId}/document/${this.document.id}`
        const config = this.$axios.agilisRequestConfig(url, window.envmap.VUE_APP_AGILIS_CONFIGURATOR_ENDPOINT)

        const response = (await this.$axios.get(url, config))
        const serverSideJson = response.data || this.createDocumentRoot()

        this.currentVersion = serverSideJson.version

        // Decora con i campi tecnici necessari per la corretta interpretazione nodo => schema
        this.json = this.addTechnicalFields(serverSideJson)

        if (this.json._checkoutFlag && this.json._lastUpdatedUser === this.userId) {
          this.autoSave = setInterval(() => {
            this.autoSaveTree()
          }, this.json._autoSaveMinutes ? this.json._autoSaveMinutes * 60000 : 300000)
        }
      } catch (e) {
        this.notifyError(e)
      }
      this.stopLoader(loader)
    },

    async autoSaveTree () {
      if (this.json._checkoutFlag && this.json._lastUpdatedUser === this.userId) {
        const loader = { id: 'save-json-document' }

        setTimeout(async () => { // Why setTimeout? Make sure blur on typing input to complete the model updates
          // Update technical fields
          this.json._lastUpdatedUser = this.userId
          this.json._lastUpdatedTime = (new Date()).toISOString()

          try {
            this.startLoader(loader)

            // Clean up from not needed technical fields inside each node
            const output = this.removeTechnicalFields(L.cloneDeep(this.json))
            this.currentVersion = output.version

            // Api
            const url = `/api/${this.applicationId}/document/${this.document.id}/version/${output.version}`
            const config = this.$axios.agilisRequestConfig(url, window.envmap.VUE_APP_AGILIS_CONFIGURATOR_ENDPOINT)
            await this.$axios.post(url, output, config)

            // Mak json as saved
            this.jsonChanged = false
          } catch (e) {
            this.notifyError(e)
          }

          this.stopLoader(loader) // TODO: decommentare quando togliamo setTimeout
        }, 0)
      }
    },

    createDocumentRoot () {
      const rootNodeSchema = this.getSchemaById()
      const nodeProperties = Object.entries(rootNodeSchema.properties).reduce((acc, [propName, propConfig]) => {
        acc[propName] = this.getPropertyInitialValue(propName, propConfig, rootNodeSchema)
        return acc
      }, {})

      const newRoot = {
        ...nodeProperties,
        _lastUpdatedTime: null,
        _lastUpdatedUser: null,
        _autoSaveMinutes: 5,
        _id: nodeProperties.id
      }

      setTimeout(() => {
        this.jsonChanged = true
      }, 500)

      return newRoot
    },

    addTechnicalFields (json) {
      L.eachDeep(
        json,
        (value, key, parentValue, ctx) => {
          if (Array.isArray(parentValue)) { // tech fields on array items
            const parent = ctx.parent.parent.value
            this.addNodeTechnicalFields(value, parent)
          }

          // Iterate only on array of children
          return key === 'children' || Array.isArray(parentValue)
        },
        { includeRoot: false }
      )

      return json
    },

    addNodeTechnicalFields (node, parentNode) {
      const schema = this.getChildNodeSchema(parentNode)
      const ancestors = this.getNodeAncestors(parentNode)

      node._schema = schema.id
      node._id = node.id
      node._ancestors = ancestors
    },

    removeTechnicalFields (json) {
      const blackList = [
        '_schema',
        '_id',
        '_ancestors'
      ]

      L.eachDeep(
        json,
        (value, key, parentValue) => {
          if (blackList.indexOf(key) !== -1) {
            delete parentValue[key]
          }

          // Iterate only on array of children
          return key === 'children' || Array.isArray(parentValue)
        },
        { includeRoot: false }
      )

      return json
    },

    // /////////////////////////////////////////////////////////////
    // Tree management utils
    // /////////////////////////////////////////////////////////////
    async addChildNode (node) {
      const childNodeSchema = this.getChildNodeSchema(node)
      const ancestors = this.getNodeAncestors(node)

      const nodeProperties = Object.entries(childNodeSchema.properties).reduce((acc, [propName, propConfig]) => {
        acc[propName] = this.getPropertyInitialValue(propName, propConfig, childNodeSchema)
        return acc
      }, {})

      const inputModel = {
        ...nodeProperties,
        id: ancestors.length > 1 ? `${node.id}-` : '', // provide prefix for the ID
        _schema: childNodeSchema.id,
        _ancestors: ancestors
      }

      try {
        const { columns, formRows } = this.getSchemaForm(childNodeSchema, inputModel, 'create')
        const formModel = await this.openFormDialog(
          inputModel,
          columns,
          formRows
        )

        const newNode = {
          ...inputModel,
          ...formModel,
          _id: formModel.id
        }

        node.children.push(newNode)

        this.setFocus(newNode)
      } catch (e) {
        // ...
      }
    },

    async copyNode (node) {
      const parentNode = this.getParentNode(node)
      this.copyRandomNode(node, parentNode)
    },

    openDialogDuplicate (node) {
      this.nodeToCopy = node
      this.dialogDuplicate = true
    },

    closeDialogDuplicate () {
      this.nodeToCopy = null
      this.dialogDuplicate = false
    },

    async duplicateNode () {
      if (this.nodeToCopy && this.destinationNodeId) {
        const parentNode = this.getNodeById(this.destinationNodeId)
        if (this.isValidChild(parentNode, this.nodeToCopy)) {
          let newNode = L.cloneDeep(this.nodeToCopy)
          const prefixNewId = parentNode._ancestors ? parentNode.id + '-' : ''
          newNode.id = prefixNewId + this.nodeToCopy.id.split('-').pop()
          this.addNodeTechnicalFields(newNode, parentNode) // set correct ancestors on the node
          newNode = this.addTechnicalFields(newNode) // set correct ancestors on children node
          this.copyRandomNode(newNode, parentNode, this.nodeToCopy.id)
        }
      }
      this.closeDialogDuplicate()
    },

    loadFormTree () {
      this.formTree.splice(0)
      this.formTree.push({
        id: this.json.id,
        name: this.$t(this.json.name),
        children: this.json.children ? this.loadFormTreeChildren(this.json) : undefined
      })
    },

    loadFormTreeChildren (node) {
      return node.children.reduce((acc, child) => {
        acc.push({
          id: child.id,
          name: this.$t(child.name),
          children: child.children ? this.loadFormTreeChildren(child) : undefined
        })
        return acc
      }, [])
    },

    copyRandomNode (node, parentNode, prevId) {
      const nodeIndex = parentNode.children.findIndex(n => n._id === node._id)

      // Copy node and children with name/id replace
      const newNode = L.cloneDeep(node)
      let newId
      let newName
      L.eachDeep(
        newNode,
        (value, key, parentValue) => {
          if (parentValue === undefined) {
            prevId = prevId ?? value.id

            // Verify newId does not exist yet
            let c = 0
            newId = value.id
            newName = value.name
            while (parentNode.children.find(n => n._id === newId)) {
              newId = value.id + 'CPY' + (c === 0 ? '' : c)
              newName = value.name + ' COPY' + (c === 0 ? '' : ' ' + c)
              c++
            }

            // Update the copied node
            value.id = newId
            value._id = newId
            value.name = newName
            return true
          }

          // in children...
          if (parentValue.id !== newId) {
            switch (key) {
              case 'id':
              case '_id': { // update the ID path
                parentValue[key] = replaceIdStr(value, prevId, newId)
                break
              }

              case '_ancestors': { // update the ancestors
                parentValue[key] = value.map(id => replaceIdStr(id, prevId, newId))
                break
              }

              default:
                // ...
            }
          }

          // Iterate only on array of children
          return key === 'children' || Array.isArray(parentValue)
        },
        { includeRoot: true }
      )

      parentNode.children.splice(nodeIndex + 1, 0, newNode)

      this.setFocus(newNode)
    },

    async editNode (node) {
      const schema = this.getSchemaById(node._schema)

      try {
        const parentNode = this.getParentNode(node)
        const nodeIndex = parentNode.children.findIndex(n => n._id === node._id)
        this.checkNodeConsistency(node)

        const { columns, formRows } = this.getSchemaForm(schema, node)
        const formModel = await this.openFormDialog(
          node,
          columns,
          formRows
        )

        parentNode.children[nodeIndex] = {
          ...node,
          formModel
        }
      } catch (e) {
        // ...
      }
    },

    deleteNode (node) {
      const parentNode = this.getParentNode(node)
      parentNode.children = parentNode.children.filter(n => n._id !== node._id)
      if (this.activeNodeId === node.id) {
        this.setFocus(parentNode)
      }
    },

    async checkin () {
      const loader = { id: 'load-json-document' }

      this.json._lastUpdatedUser = this.userId
      this.json._lastUpdatedTime = (new Date()).toISOString()

      try {
        this.startLoader(loader)

        const output = this.removeTechnicalFields(L.cloneDeep(this.json))
        this.currentVersion = output.version

        const url = Vue.axios.agilisUrlDocumentCheckin(this.applicationId, this.document.id)
        const config = this.$axios.agilisRequestConfig(url, window.envmap.VUE_APP_AGILIS_CONFIGURATOR_ENDPOINT)

        await this.$axios.post(url, output, config)

        clearInterval(this.autoSave)
        this.loadTree()
      } catch (e) {
        this.notifyError(e)
      }
      this.stopLoader(loader)
    },

    async checkout () {
      const loader = { id: 'load-json-document' }

      try {
        this.startLoader(loader)

        const url = Vue.axios.agilisUrlDocumentCheckout(this.applicationId, this.document.id)
        const config = this.$axios.agilisRequestConfig(url, window.envmap.VUE_APP_AGILIS_CONFIGURATOR_ENDPOINT)

        const response = (await this.$axios.get(url, config))
        const serverSideJson = response.data

        // Decora con i campi tecnici necessari per la corretta interpretazione nodo => schema
        this.json = this.addTechnicalFields(serverSideJson)

        this.autoSave = setInterval(() => {
          this.autoSaveTree()
        }, this.json._autoSaveMinutes ? this.json._autoSaveMinutes * 60000 : 300000)
        this.activeNodes.pop()
      } catch (e) {
        this.notifyError(e)
      }
      this.stopLoader(loader)
    },

    async migrateWorkpackages () {
      const loader = { id: 'save-json-document' }

      setTimeout(async () => { // Why setTimeout? Make sure blur on typing input to complete the model updates
        try {
          this.startLoader(loader)

          // Clean up from not needed technical fields inside each node
          const output = L.cloneDeep(this.json)

          // Api
          const url = this.document.preprocessApi
          const config = this.$axios.agilisRequestConfig(url)
          const response = await this.$axios.post(url, output, config)
          this.json = response.data
        } catch (e) {
          this.notifyError(e)
        }

        this.stopLoader(loader) // TODO: decommentare quando togliamo setTimeout
      }, 0)
    },

    checkNodeConsistency (node) {
      const schema = this.getSchemaById(node._schema)
      schema && Object.entries(schema.properties).forEach(([propName, propConfig]) => {
        if (!node.hasOwnProperty(propName)) {
          Vue.set(node, propName, this.getPropertyInitialValue(propName, propConfig, schema))
        }
      })
    },

    isValidChild (parent, child) {
      // TODO ripristinare il controllo con il rilascio del nuovo schema del documento delle aree
      // const schemaParent = this.getSchemaById(parent._schema)
      // if (schemaParent?.properties?.children?.items?._ref !== child._schema) {
      //   const error = {
      //     message: this.$t('error.treeEditorInvalidParent',
      //       {
      //         childType: child._schema,
      //         parentType: parent._schema || 'document'
      //       })
      //   }
      //   this.notifyError(error)
      //   return false
      // }
      if (!this.canAddChildren(parent)) {
        const error = {
          message: this.$t('error.treeEditorParentDisabled',
            {
              parentType: parent._schema || 'document'
            })
        }
        this.notifyError(error)
        return false
      }
      return true
    },

    getPropertyInitialValue (propName, propConfig, schema) {
      let initialValue
      const defaultValue = schema?.form?.columns?.[propName]?.defaultValue

      switch (propConfig.type) {
        case 'array':
          initialValue = defaultValue || []
          break

        default: initialValue = defaultValue || null
      }
      return initialValue
    },

    getNodeById (nodeId) {
      const node = L.findDeep(
        this.json,
        (value, key) => {
          if (key === 'id' && value === nodeId) {
            return true
          }
        }
      )

      return node.parent
    },

    getParentNode (node) {
      const ancestors = node._ancestors.slice(1)
      let parentNode = this.json
      while (ancestors.length > 0) {
        const parentId = ancestors.shift()
        parentNode = parentNode.children.find(n => n.id === parentId)
      }
      return parentNode
    },

    getNodeAncestors (parentNode) {
      return parentNode._ancestors ? [...parentNode._ancestors, parentNode._id] : [parentNode._id]
    },

    setFocus (node) {
      setTimeout(() => {
        this.setNodeOpen(node)
        this.activeNodes.pop()
        this.activeNodes.push(node.id)
      }, 0)
    },

    setNodeOpen (node) {
      let openNodes = Array.from(this.$refs.tree.openCache)
      let nodesToOpen = [...node._ancestors, node.id]
      nodesToOpen.forEach(node => {
        if (!openNodes.find(openNode => openNode.id === node)) {
          openNodes.push(node)
        }
      })
      this.openNodes = openNodes
    },

    activeNodeBeforeChange (delta) {
      if (delta.id) {
        if (this.activeNode?.children?.length > 0) {
          const tmp = this.activeNode.children[0].id.split('-')
          const oldId = tmp.slice(0, tmp.length - 1).join('-')
          const newId = delta.id

          if (oldId !== newId) {
            // update children
            L.eachDeep(
              this.activeNode,
              (value, key, parentValue) => {
                // in children...
                switch (key) {
                  case 'id':
                  case '_id': { // update the newID in the children path
                    parentValue[key] = replaceIdStr(value, oldId, newId)
                    break
                  }

                  case '_ancestors': { // update the ancestors
                    parentValue[key] = value.map(id => replaceIdStr(id, oldId, newId))
                    break
                  }

                  default:
                    // ...
                }

                // Iterate only on array of children
                return key === 'children' || Array.isArray(parentValue)
              },
              { includeRoot: false }
            )

            /* The code below is a workaround to be able to dynamcally change ids if tree elements */
            // 1) persist in a instance variable the open ids (by replacing the changed value too)
            this.openNodes = Array.from(this.$refs.tree.openCache)
              .map(id => replaceIdStr(id, oldId, newId))

            this.forceUnmount = true // 2) unmount the form and wait a little with a setTimeout to be unmounted
            setTimeout(() => {
              this.activeNode._id = newId // 3) update the technical id on the active node
              this.activeNodes.pop() // 4) reset the activeNodes array
              this.forceUnmount = false // 5) restore the tree and wait a little with a setTimeout to be mounted
              setTimeout(() => {
                this.activeNodes.push(newId) // 6) restore the active node
              }, 100)
            }, 100)
          }
        }
      }

      const fieldConfig = this.activeNodeForm.fields[Object.keys(delta)[0]]
      if (fieldConfig.beforeChange?.method && typeof this[fieldConfig.beforeChange.method] === 'function') {
        this[fieldConfig.beforeChange.method](fieldConfig.beforeChange)
      }

      return delta
    },

    // /////////////////////////////////////////////////////////////
    // Schema management utils
    // /////////////////////////////////////////////////////////////
    getSchemaById (schemaId = this.schema._ref) {
      if (!schemaId) return this.schema
      const s = Object.values(this.schema._defs).find(def => def.id === schemaId)

      if (!s) console.error('Not able to find schema:', schemaId)

      return s
    },

    getChildNodeSchema (node) {
      const nodeSchema = this.getSchemaById(node._schema)

      if (nodeSchema.properties.children.items.type === 'object') {
        return nodeSchema.properties.children.items.type
      } else if (nodeSchema.properties.children.items._ref) {
        return this.getSchemaById(nodeSchema.properties.children.items._ref)
      }
    },

    getSchemaForm (schema, node, mode = null) {
      const columns = schema?.form?.columns || {}
      const formRows = schema?.form?.formRows || []
      const itemDecoration = schema?.form?.itemDecoration || {}

      if (columns.id && node._ancestors) { // TODO: questo è un hardcoding perchè scrive il validator dinamico sul nodo i-esimo
        const prefix = node._ancestors.length > 1 ? `${node._ancestors[node._ancestors.length - 1]}-` : ''
        columns.id = {
          ...columns.id,
          placeholder: prefix,
          rules: {
            unique_id: [this.json, mode],
            regex_id: '^' + prefix + '[A-Z0-9]+$'
          }
        }
      }

      if (columns.version && !node._ancestors) {
        columns.version = {
          ...columns.version,
          rules: {
            version_higher: this.currentVersion,
            regex: '^(\\d+\\.)?(\\d+\\.)?(\\*|\\d+)$'
          }
        }
      }

      // Inject datatable config form childNode schema
      Object.values(columns)
        .filter((c) => c.type === 'datatable')
        .forEach(c => {
          const fieldId = c.id
          const childSchemaId = schema.properties[fieldId].items._ref
          const childSchema = this.getSchemaById(childSchemaId)

          columns[fieldId].columns = childSchema.form.columns
          columns[fieldId].formRows = childSchema.form.formRows
          columns[fieldId].itemDecoration = childSchema.form.itemDecoration
        })

      return {
        columns,
        formRows,
        itemDecoration
      }
    },

    // /////////////////////////////////////////////////////////////
    // Permissions utils
    // /////////////////////////////////////////////////////////////
    canAddChildren (node) {
      const nodeSchema = this.getSchemaById(node._schema)

      const disabledLogic = nodeSchema?.properties?.children?.disabled?.jsonLogic
      const disabled = disabledLogic ? jsonLogic.apply(disabledLogic, node) : false

      return nodeSchema?.properties?.children?.type === 'array' && !disabled
    },

    canDeleteNode (node) {
      return node.id !== this.document.id
    },

    canEditNode (node) {
      const nodeSchema = this.getSchemaById(node._schema)
      return nodeSchema.form
    },

    canCopyNode (node) {
      const nodeSchema = this.getSchemaById(node._schema)
      return node.id !== this.document.id && nodeSchema.form
    },

    userCanDoDocumentOperation (operation) {
      return Configuration.auth.userCanDoDocumentOperation(this.selectedContext, operation)
    },

    toggleTreeview () {
      this.expanded = !this.expanded
      this.$refs.tree.updateAll(this.expanded)
    },

    popupWarning (fieldConfig) {
      this.notifyMessage(this.$t(fieldConfig.message))
    }
  }
}
